import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField, MenuItem,
    Button, Grid, LinearProgress, Typography, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControlLabel, Checkbox, Select, FormControl, InputLabel,
    Chip
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const PayElements = ({ setIsLoggedIn }) => {
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [calcBases, setCalcBases] = useState([]);
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        element_code: '',
        element_name: '',
        element_type: 'EARNING',
        element_category: 'RECURRING',
        calculation_basis_id: '',
        is_taxable: 1,
        is_ssb_applicable: 1,
        prorate_on_unpaid_leave: 1,
        prorate_on_joining: 1,
        is_fixed_pay: 0,
        unpaid_leave_impact: 1.0,
        medical_leave_impact: 1.0,
        is_active: 1
    });


    const fetchElements = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) setRowData(response.data.data);
        } catch (error) {
            console.error("Error fetching elements:", error);
            setStatusMsg({ open: true, msg: 'Error fetching elements', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBases = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) setCalcBases(response.data.data);
        } catch (error) {
            console.error("Error fetching bases:", error);
        }
    }, []);

    useEffect(() => {
        fetchElements();
        fetchBases();
    }, [fetchElements, fetchBases]);

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                element_code: item.element_code,
                element_name: item.element_name,
                element_type: item.element_type,
                element_category: item.element_category || 'RECURRING',
                calculation_basis_id: item.calculation_basis_id || '',
                is_taxable: item.is_taxable,
                is_ssb_applicable: item.is_ssb_applicable,
                prorate_on_unpaid_leave: item.prorate_on_unpaid_leave,
                prorate_on_joining: item.prorate_on_joining,
                is_fixed_pay: item.is_fixed_pay || 0,
                unpaid_leave_impact: item.unpaid_leave_impact || 1.0,
                medical_leave_impact: item.medical_leave_impact || 1.0,
                is_active: item.is_active
            });

        } else {
            setEditingItem(null);
            setFormData({
                element_code: '',
                element_name: '',
                element_type: 'EARNING',
                element_category: 'RECURRING',
                calculation_basis_id: '',
                is_taxable: 1,
                is_ssb_applicable: 1,
                prorate_on_unpaid_leave: 1,
                prorate_on_joining: 1,
                is_fixed_pay: 0,
                unpaid_leave_impact: 1.0,
                medical_leave_impact: 1.0,
                is_active: 1
            });

        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const url = editingItem
                ? `${process.env.REACT_APP_API_URL1}/sm/payroll/elements/${editingItem.id}`
                : `${process.env.REACT_APP_API_URL1}/sm/payroll/elements`;

            const method = editingItem ? 'put' : 'post';

            const response = await axios[method](url, formData, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });

            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Saved successfully', severity: 'success' });
                setDialogOpen(false);
                fetchElements();
            }
        } catch (error) {
            console.error("Error saving element:", error);
            setStatusMsg({ open: true, msg: 'Error saving element', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this pay element?")) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements/${id}`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Deleted successfully', severity: 'success' });
                fetchElements();
            }
        } catch (error) {
            console.error("Error deleting element:", error);
            setStatusMsg({ open: true, msg: 'Error deleting data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const columnDefs = [
        { field: "element_code", headerName: "Code", width: 120 },
        { field: "element_name", headerName: "Name", width: 220 },
        {
            field: "element_type",
            headerName: "Type",
            width: 130,
            cellRenderer: p => (
                <Chip
                    label={p.value}
                    size="small"
                    color={p.value === 'EARNING' ? 'success' : 'error'}
                    variant="outlined"
                />
            )
        },
        {
            field: "element_category",
            headerName: "Category",
            width: 140,
            cellRenderer: p => (
                <Chip
                    label={p.value}
                    size="small"
                    color={p.value === 'RECURRING' ? 'primary' : p.value === 'ATTENDANCE' ? 'info' : 'warning'}
                    variant="outlined"
                />
            )
        },
        {
            headerName: "Basis",
            width: 180,
            valueGetter: p => {
                const b = calcBases.find(x => x.id === p.data.calculation_basis_id);
                return b ? b.basis_name : 'N/A';
            }
        },
        { field: "is_taxable", headerName: "Tax?", width: 90, cellRenderer: p => p.value ? 'Yes' : 'No' },
        { field: "is_ssb_applicable", headerName: "SSB?", width: 90, cellRenderer: p => p.value ? 'Yes' : 'No' },
        { field: "prorate_on_unpaid_leave", headerName: "Prorate Lv?", width: 110, cellRenderer: p => p.value ? 'Yes' : 'No' },
        { field: "prorate_on_joining", headerName: "Prorate Jn?", width: 110, cellRenderer: p => p.value ? 'Yes' : 'No' },
        { field: "is_fixed_pay", headerName: "Fixed?", width: 90, cellRenderer: p => p.value ? 'Yes' : 'No' },
        { field: "is_active", headerName: "Active?", width: 90, cellRenderer: p => p.value ? 'Yes' : 'No' },
        {
            headerName: "Actions",

            width: 150,
            cellRenderer: p => (
                <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(p.data)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(p.data.id)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Pay Elements" setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'hidden' }}>
                <Card sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight="bold">Master Data: Pay Elements</Typography>
                        <Stack direction="row" spacing={2}>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchElements}>Refresh</Button>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ bgcolor: '#1a237e' }}>
                                Add New Element
                            </Button>
                        </Stack>
                    </Stack>
                </Card>

                {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

                <Card sx={{ height: 'calc(100% - 130px)', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Box className="ag-theme-quartz" sx={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={{ sortable: true, filter: true, resizable: true }}
                        />
                    </Box>
                </Card>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingItem ? 'Edit Pay Element' : 'Add New Pay Element'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="Element Code" size="small"
                                value={formData.element_code}
                                onChange={e => setFormData({ ...formData, element_code: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="Element Name" size="small"
                                value={formData.element_name}
                                onChange={e => setFormData({ ...formData, element_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                    label="Type"
                                    value={formData.element_type}
                                    onChange={e => setFormData({ ...formData, element_type: e.target.value })}
                                >
                                    <MenuItem value="EARNING">Earning</MenuItem>
                                    <MenuItem value="DEDUCTION">Deduction</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    label="Category"
                                    value={formData.element_category}
                                    onChange={e => setFormData({ ...formData, element_category: e.target.value })}
                                >
                                    <MenuItem value="RECURRING">Recurring (Monthly)</MenuItem>
                                    <MenuItem value="ATTENDANCE">Attendance Linked</MenuItem>
                                    <MenuItem value="ONE_TIME">One-time / Ad-hoc</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Calculation Basis</InputLabel>
                                <Select
                                    label="Calculation Basis"
                                    value={formData.calculation_basis_id}
                                    onChange={e => setFormData({ ...formData, calculation_basis_id: e.target.value })}
                                >
                                    <MenuItem value="">None / Manual</MenuItem>
                                    {calcBases.map(b => <MenuItem key={b.id} value={b.id}>{b.basis_name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.is_taxable === 1} onChange={e => setFormData({ ...formData, is_taxable: e.target.checked ? 1 : 0 })} />}
                                label="Taxable"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.is_ssb_applicable === 1} onChange={e => setFormData({ ...formData, is_ssb_applicable: e.target.checked ? 1 : 0 })} />}
                                label="SSB Applicable"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.prorate_on_unpaid_leave === 1} onChange={e => setFormData({ ...formData, prorate_on_unpaid_leave: e.target.checked ? 1 : 0 })} />}
                                label="Prorate Leave"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.prorate_on_joining === 1} onChange={e => setFormData({ ...formData, prorate_on_joining: e.target.checked ? 1 : 0 })} />}
                                label="Prorate Joining"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.is_fixed_pay === 1} onChange={e => setFormData({ ...formData, is_fixed_pay: e.target.checked ? 1 : 0 })} />}
                                label="Fixed Pay"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.is_active === 1} onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })} />}
                                label="Active"
                            />
                        </Grid>

                        {formData.element_category === 'RECURRING' && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mt: 1, borderBottom: '1px solid #eee', pb: 1, fontWeight: 'bold' }}>
                                        Advanced Proration Logic
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth label="Unpaid Leave Impact (Ratio)" size="small" type="number"
                                        helperText="1.0 = Deduct full day pay per leave day"
                                        value={formData.unpaid_leave_impact}
                                        onChange={e => setFormData({ ...formData, unpaid_leave_impact: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth label="Medical Leave Impact (Ratio)" size="small" type="number"
                                        helperText="0.5 = Deduct half day pay per leave day"
                                        value={formData.medical_leave_impact}
                                        onChange={e => setFormData({ ...formData, medical_leave_impact: e.target.value })}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1a237e' }}>Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={6000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
            >
                <Alert severity={statusMsg.severity} sx={{ width: '100%' }}>{statusMsg.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default PayElements;
